import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  Send,
  UserCheck,
  UserPlus,
  UserX
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabaseClient } from '../lib/supabaseClient';
import { compareResidentsByKana } from '../lib/residentSort';

type EntryMode = 'admission' | 'departure';

type ActiveResidentOption = {
  id: string;
  name: string;
  kana: string | null;
  room: string | null;
  care_level: string | null;
  admission_date: string | null;
};

type EntryOptions = {
  activeResidents: ActiveResidentOption[];
  clinicOptions: string[];
  stats?: {
    activeResidents?: number;
  };
};

type AdmissionForm = {
  name: string;
  kana: string;
  admission_date: string;
  care_level: string;
  gender: string;
  birth_date: string;
  care_insurance_expiry_date: string;
  primary_clinic: string;
  emergency_hospital: string;
  admission_source_note: string;
  notes: string;
};

type DepartureForm = {
  resident_id: string;
  resident_name: string;
  departure_date: string;
  departure_care_level: string;
  departure_reason: string;
  departure_note: string;
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyAdmission = (): AdmissionForm => ({
  name: '',
  kana: '',
  admission_date: today(),
  care_level: '',
  gender: '',
  birth_date: '',
  care_insurance_expiry_date: '',
  primary_clinic: '',
  emergency_hospital: '',
  admission_source_note: '',
  notes: ''
});

const emptyDeparture = (): DepartureForm => ({
  resident_id: '',
  resident_name: '',
  departure_date: today(),
  departure_care_level: '',
  departure_reason: '',
  departure_note: ''
});

const careLevelOptions = ['', '要支援1', '要支援2', '要介護1', '要介護2', '要介護3', '要介護4', '要介護5'];
const defaultClinicOptions = ['ハートフル熊谷', 'トータルケアクリニック', '根本医院'];
const departureReasons = ['', '入院', '看取り', '転居', '施設移動', 'その他'];

const ResidentEntryPage = () => {
  const [mode, setMode] = useState<EntryMode>('admission');
  const [options, setOptions] = useState<EntryOptions>({ activeResidents: [], clinicOptions: defaultClinicOptions });
  const [admission, setAdmission] = useState<AdmissionForm>(() => emptyAdmission());
  const [departure, setDeparture] = useState<DepartureForm>(() => emptyDeparture());
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'review' | 'error'; text: string } | null>(null);

  const selectedResident = useMemo(
    () => options.activeResidents.find(resident => resident.id === departure.resident_id) ?? null,
    [departure.resident_id, options.activeResidents]
  );

  const loadOptions = async () => {
    if (!supabaseClient) {
      setMessage({ type: 'error', text: 'Supabaseの設定が見つかりません。' });
      return;
    }

    setLoadingOptions(true);
    try {
      const { data, error } = await supabaseClient.rpc('resident_employee_get_options');
      if (error) throw error;
      const nextOptions = data as EntryOptions;
      setOptions({
        activeResidents: [...(nextOptions.activeResidents ?? [])].sort(compareResidentsByKana),
        clinicOptions: nextOptions.clinicOptions?.length ? nextOptions.clinicOptions : defaultClinicOptions,
        stats: nextOptions.stats
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message ?? String(error) });
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    document.title = '入退去登録 | さつきホーム';
    void loadOptions();
  }, []);

  const submitAdmission = async () => {
    if (!supabaseClient) return;
    if (!admission.name.trim()) {
      setMessage({ type: 'error', text: '入居者名を入力してください。' });
      return;
    }
    if (!admission.kana.trim()) {
      setMessage({ type: 'error', text: 'ふりがなを入力してください。' });
      return;
    }
    if (!admission.admission_date) {
      setMessage({ type: 'error', text: '入居日を入力してください。' });
      return;
    }

    const { data, error } = await supabaseClient.rpc('resident_employee_submit_admission', {
      _payload: admission
    });
    if (error) throw error;

    const result = data as { status?: string; message?: string };
    setAdmission(emptyAdmission());
    setMessage({ type: 'success', text: result.message ?? '入居登録しました。' });
    await loadOptions();
  };

  const submitDeparture = async () => {
    if (!supabaseClient) return;
    const residentName = selectedResident?.name ?? departure.resident_name;
    if (!departure.resident_id && !residentName.trim()) {
      setMessage({ type: 'error', text: '退居者名を入力してください。' });
      return;
    }
    if (!departure.departure_date) {
      setMessage({ type: 'error', text: '退居日を入力してください。' });
      return;
    }

    const { data, error } = await supabaseClient.rpc('resident_employee_submit_departure', {
      _payload: {
        ...departure,
        resident_name: residentName
      }
    });
    if (error) throw error;

    const result = data as { status?: string; message?: string };
    setDeparture(emptyDeparture());
    setMessage({
      type: result.status === 'needs_review' ? 'review' : 'success',
      text: result.message ?? '退居登録しました。'
    });
    await loadOptions();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      if (mode === 'admission') {
        await submitAdmission();
      } else {
        await submitDeparture();
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message ?? String(error) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-lg bg-cyan-700 text-white">
              <ClipboardCheck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">入退去登録</h1>
              <p className="text-sm text-slate-500">さつきホーム</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600 sm:flex">
              <UserCheck className="h-4 w-4" />
              入居中 {options.stats?.activeResidents ?? options.activeResidents.length}名
            </div>
            <Link to="/care" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <ArrowLeft className="h-4 w-4" />
              ケアトップ
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-5 sm:px-6">
        {message && (
          <div
            className={`mb-4 flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${
              message.type === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : message.type === 'review'
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
          >
            {message.type === 'error' ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{message.text}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4">
            <div className="grid grid-cols-2 rounded-lg border border-slate-200 bg-slate-50 p-1">
              <ModeButton active={mode === 'admission'} icon={<UserPlus className="h-4 w-4" />} label="入居登録" onClick={() => setMode('admission')} />
              <ModeButton active={mode === 'departure'} icon={<UserX className="h-4 w-4" />} label="退居登録" onClick={() => setMode('departure')} />
            </div>
          </div>

          <div className="p-4 sm:p-5">
            {mode === 'admission' ? (
              <AdmissionFields form={admission} clinicOptions={options.clinicOptions} onChange={setAdmission} />
            ) : (
              <DepartureFields
                form={departure}
                residents={options.activeResidents}
                loading={loadingOptions}
                selectedResident={selectedResident}
                onChange={setDeparture}
                onReload={loadOptions}
              />
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-4">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-cyan-700 px-4 py-3 text-sm font-semibold text-white hover:bg-cyan-800 disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              登録する
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

const ModeButton = ({ active, icon, label, onClick }: { active: boolean; icon: ReactNode; label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${
      active ? 'bg-white text-cyan-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'
    }`}
  >
    {icon}
    {label}
  </button>
);

const AdmissionFields = ({ form, clinicOptions, onChange }: { form: AdmissionForm; clinicOptions: string[]; onChange: (form: AdmissionForm) => void }) => (
  <div className="grid gap-4 md:grid-cols-2">
    <TextField label="入居者名" value={form.name} required onChange={value => onChange({ ...form, name: value })} />
    <TextField label="ふりがな" value={form.kana} required onChange={value => onChange({ ...form, kana: value })} />
    <TextField label="入居日" type="date" value={form.admission_date} required onChange={value => onChange({ ...form, admission_date: value })} />
    <SelectField label="介護度" value={form.care_level} options={careLevelOptions} onChange={value => onChange({ ...form, care_level: value })} />
    <SelectField label="性別" value={form.gender} options={['', '女性', '男性', 'その他']} onChange={value => onChange({ ...form, gender: value })} />
    <TextField label="生年月日" type="date" value={form.birth_date} onChange={value => onChange({ ...form, birth_date: value })} />
    <TextField label="介護保険証の有効期限" type="date" value={form.care_insurance_expiry_date} onChange={value => onChange({ ...form, care_insurance_expiry_date: value })} />
    <SelectField label="担当クリニック" value={form.primary_clinic} options={['', ...clinicOptions]} onChange={value => onChange({ ...form, primary_clinic: value })} />
    <TextField label="緊急時の病院" value={form.emergency_hospital} onChange={value => onChange({ ...form, emergency_hospital: value })} />
    <TextArea label="入居元/共有事項" value={form.admission_source_note} onChange={value => onChange({ ...form, admission_source_note: value })} />
    <TextArea label="メモ" value={form.notes} onChange={value => onChange({ ...form, notes: value })} />
  </div>
);

const DepartureFields = ({
  form,
  residents,
  loading,
  selectedResident,
  onChange,
  onReload
}: {
  form: DepartureForm;
  residents: ActiveResidentOption[];
  loading: boolean;
  selectedResident: ActiveResidentOption | null;
  onChange: (form: DepartureForm) => void;
  onReload: () => void;
}) => (
  <div className="grid gap-4 md:grid-cols-2">
    <label className="block md:col-span-2">
      <span className="mb-1 block text-xs font-medium text-slate-500">退居者</span>
      <div className="flex gap-2">
        <select
          name="退居者"
          value={form.resident_id}
          onChange={event => onChange({ ...form, resident_id: event.target.value, resident_name: '' })}
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-700"
        >
          <option value="">一覧にない場合は氏名入力</option>
          {residents.map(resident => (
            <option key={resident.id} value={resident.id}>
              {resident.name}{resident.room ? ` / ${resident.room}` : ''}{resident.care_level ? ` / ${resident.care_level}` : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onReload}
          disabled={loading}
          className="grid h-10 w-10 place-items-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          aria-label="入居者一覧を更新"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
        </button>
      </div>
    </label>

    {!form.resident_id && (
      <TextField label="退居者名" value={form.resident_name} required onChange={value => onChange({ ...form, resident_name: value })} />
    )}

    {selectedResident && (
      <div className="rounded-lg border border-cyan-100 bg-cyan-50 px-3 py-2 text-sm text-cyan-900 md:col-span-2">
        {selectedResident.name} / {selectedResident.care_level || '介護度未設定'} / 入居日 {selectedResident.admission_date?.slice(0, 10) || '-'}
      </div>
    )}

    <TextField label="退居日" type="date" value={form.departure_date} required onChange={value => onChange({ ...form, departure_date: value })} />
    <SelectField label="退居時の介護度" value={form.departure_care_level} options={careLevelOptions} onChange={value => onChange({ ...form, departure_care_level: value })} />
    <SelectField label="退居理由" value={form.departure_reason} options={departureReasons} onChange={value => onChange({ ...form, departure_reason: value })} />
    <TextArea label="共有事項" value={form.departure_note} onChange={value => onChange({ ...form, departure_note: value })} />
  </div>
);

const TextField = ({
  label,
  value,
  onChange,
  type = 'text',
  required = false
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
    <input
      type={type}
      name={label}
      value={value}
      required={required}
      onChange={event => onChange(event.target.value)}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base outline-none focus:border-cyan-700 sm:text-sm"
    />
  </label>
);

const TextArea = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => (
  <label className="block md:col-span-2">
    <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
    <textarea
      name={label}
      value={value}
      onChange={event => onChange(event.target.value)}
      rows={4}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base outline-none focus:border-cyan-700 sm:text-sm"
    />
  </label>
);

const SelectField = ({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
    <select
      name={label}
      value={value}
      onChange={event => onChange(event.target.value)}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base outline-none focus:border-cyan-700 sm:text-sm"
    >
      {options.map(option => (
        <option key={option || 'empty'} value={option}>{option || '未選択'}</option>
      ))}
    </select>
  </label>
);

export default ResidentEntryPage;
